import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
import Home from './components/Home';
import Landing from './components/LandingPage';
import About from './pages/About';
import TourPage from './pages/TourPage';
import { TourProvider } from './context/TourContext';
import FAQ from './pages/FAQ';
import Help from './pages/Help';
import AboutModerators from './pages/AboutModerators';
import Privacy from './pages/Privacy';
import DeleteAccount from './pages/DeleteAccount';
import Support from './pages/Support';
import Press from './pages/Press';
import HowItWorks from './pages/HowItWorks';
import AppReview from './pages/AppReview';
import TestClusteringPage from './components/TestClusteringPage';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminAreaReports from './components/AdminAreaReports';
import AdminAreaReportPrint from './components/AdminAreaReportPrint';
import AdminModeratorCoverageMap from './components/AdminModeratorCoverageMap';
import ModeratorReports from './components/ModeratorReports';
import ModeratorHiddenQueue from './components/ModeratorHiddenQueue';
import ModeratorActions from './components/ModeratorActions';
import ModeratorMessages from './components/ModeratorMessages';
import { LocationProvider } from './context/LocationContext';
import { AdminProvider, useAdmin } from './context/AdminContext';
import './index.css';
import './components/landingPageStyles.css';
import './components/dashboardStyles.css';
import './components/adminStyles.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state
  const navigate = useNavigate();

  // Load user data on initial render
  useEffect(() => {
    setLoading(true);
    
    // Check if user is already logged in (token exists)
    const token = localStorage.getItem('token');
    
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return;
    }
    
    // Get user data from localStorage if available
    const userData = localStorage.getItem('user');
    
    if (!userData || userData === 'undefined') {
      console.error('User data missing or undefined');
      // Clear invalid data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      
      if (!parsedUser || typeof parsedUser !== 'object' || !parsedUser.id) {
        console.error('Invalid user data format');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        setUser(null);
      } else {
        setIsAuthenticated(true);
        setUser(parsedUser);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setUser(null);
    }
    
    setLoading(false);
  }, []);

  const login = (userData, token) => {
    if (!userData || !userData.id) {
      console.error('Invalid user data received');
      alert('Login failed: Invalid user data received');
      return;
    }
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    setIsAuthenticated(true);
    navigate('/dashboard');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    navigate('/');
  };

  // Show loading state while checking authentication
  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }
  
  return (
    <AdminProvider>
      <TourProvider>
      <LocationProvider>
        <div className="container">
          <Routes>
            {/* Public Documentation Pages */}
            <Route 
              path="/" 
              element={<Landing />} 
            />
            <Route 
              path="/faq" 
              element={<FAQ />} 
            />
            <Route 
              path="/help" 
              element={<Help />} 
            />
            <Route
              path="/about/moderators"
              element={<AboutModerators />}
            />
            <Route
              path="/about"
              element={<About />}
            />
            <Route
              path="/tour"
              element={<TourPage />}
            />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/support" element={<Support />} />
            <Route path="/press" element={<Press />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/app-review" element={<AppReview />} />
            
            {/* User Routes */}
            <Route 
              path="/dashboard" 
              element={isAuthenticated && user ? <Home user={user} logout={logout} /> : <Navigate to="/login" />} 
            />
            <Route 
              path="/login" 
              element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login login={login} />} 
            />
            <Route 
              path="/register" 
              element={isAuthenticated ? <Navigate to="/dashboard" /> : <Register login={login} />} 
            />
            
            {/* Admin Routes */}
            <Route 
              path="/admin/login" 
              element={<AdminLogin />} 
            />
            <Route 
              path="/admin/dashboard" 
              element={<ProtectedAdminRoute />} 
            />

            <Route
              path="/admin/area-reports"
              element={<ProtectedAdminRoute element={<AdminAreaReports />} />}
            />
            <Route
              path="/admin/area-reports/print/:id"
              element={<ProtectedAdminRoute element={<AdminAreaReportPrint />} />}
            />

            <Route
              path="/admin/moderator-coverage"
              element={<ProtectedAdminRoute element={<AdminModeratorCoverageMap />} />}
            />
            
            {/* Moderator Routes - Restricted to moderators only */}
            <Route 
              path="/moderator/dashboard" 
              element={isAuthenticated && (user?.role === 'admin' || (user?.role === 'moderator' && (user?.moderatorActive || user?.moderator))) ? 
                <ModeratorReports onLogout={logout} /> : 
                <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/moderator/hidden" 
              element={isAuthenticated && (user?.role === 'admin' || (user?.role === 'moderator' && (user?.moderatorActive || user?.moderator))) ? 
                <ModeratorHiddenQueue onLogout={logout} /> : 
                <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/moderator/actions" 
              element={isAuthenticated && (user?.role === 'admin' || (user?.role === 'moderator' && (user?.moderatorActive || user?.moderator))) ? 
                <ModeratorActions onLogout={logout} /> : 
                <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/moderator/messages" 
              element={isAuthenticated && (user?.role === 'admin' || (user?.role === 'moderator' && (user?.moderatorActive || user?.moderator))) ? 
                <ModeratorMessages onLogout={logout} /> : 
                <Navigate to="/dashboard" />} 
            />
            
            {/* Testing Routes */}
            <Route 
              path="/test/clustering" 
              element={<TestClusteringPage />} 
            />
          </Routes>
        </div>
      </LocationProvider>
      </TourProvider>
    </AdminProvider>
  );
}

// Protected route component for admin
function ProtectedAdminRoute({ element = null }) {
  const { isAdminAuthenticated, loading } = useAdmin();
  
  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }
  
  if (!isAdminAuthenticated) {
    return <Navigate to="/admin/login" />;
  }

  return element || <AdminDashboard />;
}

export default App;
